/**
 * MetricTable — the DS metric grid «аккаунты × дни» (BLOGGER-CARD-DS, spec S4).
 *
 * Promoted out of src/components/MonitoringMatrix.jsx (R7 F3/F4) into the design-system
 * layer: same module, same props, same internals — a real <table> with a sticky first
 * column, horizontally scrolling day columns, today's-column highlight, account→video
 * row expansion and the «Итого» row. The DS Table family (flex divs, exactly one
 * flexible column) cannot express that shape, which is why this is a tenth export
 * rather than a Table variant (spec S4 / decision D2).
 *
 * What changed in the promotion, and nothing else: the legacy matrix class family became
 * `oc-metric-table*` 1:1 and its rules moved into ds.css under .outcrm-scope, de-hexed;
 * the three dingbats (play, caret-down, caret-right) became Phosphor Icon call sites,
 * clearing the bucket the queue-1 spec deferred. Two consumers on day one —
 * /monitoring (MonitoringView) and the blogger card's Мониторинг tab.
 *
 * Pure presentation over the `matrix` object of GET /packs/:id/rounds/:roundId/daily
 * ({from, to, days[], accounts:[{accountId, cells, videos}], totals}). The window
 * принцип (F4): the component knows nothing about rounds — a closed round just
 * passes `closedAt` so the closing-day column gets the highlight instead of today.
 *
 * Cell maps are SPARSE — a missing day means zeros. Account handle/platform come
 * from the sibling `accounts` meta array of the same response (matrix rows carry
 * only accountId), joined client-side here.
 *
 * Account rows expand to per-video rows (account-level chevron only; videos have
 * no expander — F3). Long windows just scroll horizontally; the first column is
 * sticky (weekly-stats sticky idiom).
 *
 * SCOPE-AMBSTAT-VIEW §3.11 — `formatColumn` (default `fmtDayShort`) is the ONE thing the
 * hourly matrix of the lead card changes here: its axis carries 'YYYY-MM-DDTHH' keys, and
 * the day formatter slices DD.MM out of them, i.e. 24 identical headers. The matrix shape
 * itself is unchanged — `days` simply holds hour keys.
 */
import { useState } from 'react';
import { Icon } from './Icon.jsx';

const fmtRU = new Intl.NumberFormat('ru-RU');

/** Compact views formatter. */
export function fmtViewsCompact (n) {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return '0';
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1).replace('.', ',')}M`;
  if (v >= 1_000)     return `${(v / 1_000).toFixed(1).replace('.', ',')}К`;
  return fmtRU.format(v);
}

/** 'YYYY-MM-DD…' | Date → 'DD.MM' (no Date parsing — day strings are UTC-safe as-is). */
export function fmtDayShort (value) {
  if (!value) return '—';
  const s = value instanceof Date ? value.toISOString() : String(value);
  return `${s.slice(8, 10)}.${s.slice(5, 7)}`;
}

const PLATFORM_LABEL = {
  instagram: 'IG',
  tiktok:    'TT',
  youtube:   'YT',
  facebook:  'FB',
};

function deltaLabel (delta) {
  if (!delta) return '';
  return delta > 0 ? `+${fmtViewsCompact(delta)}` : `-${fmtViewsCompact(Math.abs(delta))}`;
}

function AccountDayCell ({ cell, highlighted }) {
  const posts = cell?.postsPublished ?? 0;
  const delta = cell?.viewsDelta ?? 0;
  return (
    <td className={`oc-metric-table__cell${highlighted ? ' oc-metric-table__cell--hl' : ''}`}>
      {posts > 0
        ? <span className="oc-metric-table__posts">{posts}</span>
        : <span className="oc-metric-table__dash">—</span>}
      {delta !== 0 && <span className="oc-metric-table__delta">{deltaLabel(delta)}</span>}
    </td>
  );
}

function VideoDayCell ({ cell, highlighted, isPubDay }) {
  if (!cell) {
    if (isPubDay) {
      return (
        <td className="oc-metric-table__cell oc-metric-table__cell--pub">
          <span className="oc-metric-table__pub"><Icon name="play" size="sm" /></span>
        </td>
      );
    }
    return <td className={`oc-metric-table__cell${highlighted ? ' oc-metric-table__cell--hl' : ''}`} />;
  }
  if (cell.isPublicationDay) {
    return (
      <td className="oc-metric-table__cell oc-metric-table__cell--pub">
        <span className="oc-metric-table__pub"><Icon name="play" size="sm" /> {fmtViewsCompact(cell.viewsEnd)}</span>
      </td>
    );
  }
  return (
    <td className={`oc-metric-table__cell${highlighted ? ' oc-metric-table__cell--hl' : ''}`}>
      <span className="oc-metric-table__views">{fmtViewsCompact(cell.viewsEnd)}</span>
      {cell.viewsDelta !== 0 && <span className="oc-metric-table__delta">{deltaLabel(cell.viewsDelta)}</span>}
    </td>
  );
}

function VideoRow ({ video, days, hlDay, showMedian }) {

  let total = 0;
  for (let i = days.length - 1; i >= 0; i--) {
    const c = video.cells[days[i]];
    if (c) { total = c.viewsEnd; break; }
  }
  const label = `видео ${fmtDayShort(video.publishedAt)}`;
  return (
    <tr className="oc-metric-table__row oc-metric-table__row--video">
      <td className="oc-metric-table__td--sticky oc-metric-table__video-name">
        {video.link
          ? <a href={video.link} target="_blank" rel="noreferrer" className="oc-metric-table__video-link">{label}</a>
          : label}
      </td>
      <td className="oc-metric-table__cell" />
      <td className="oc-metric-table__cell"><b>{fmtViewsCompact(total)}</b></td>
      {showMedian && <td className="oc-metric-table__cell" />}
      {days.map((day) => (
        <VideoDayCell
          key={day}
          cell={video.cells[day]}
          highlighted={day === hlDay}
          isPubDay={video.publishedAt === day}
        />
      ))}
    </tr>
  );
}

function AccountRows ({ account, meta, days, hlDay, target, expanded, onToggle, showMedian, median }) {
  const platform = PLATFORM_LABEL[meta?.platform] ?? meta?.platform ?? '?';
  const handle = meta?.handle ? `@${meta.handle}` : account.accountId;
  const hasVideos = account.videos.length > 0;

  let posts = 0;
  let views = 0;
  for (const day of days) {
    const c = account.cells[day];
    if (!c) continue;
    posts += c.postsPublished;
    views += c.viewsDelta;
  }

  return (
    <>
      <tr
        className={`oc-metric-table__row oc-metric-table__row--account${hasVideos ? ' oc-metric-table__row--clickable' : ''}`}
        onClick={hasVideos ? onToggle : undefined}
      >
        <td className="oc-metric-table__td--sticky oc-metric-table__account-name">
          <span className="oc-metric-table__chevron">
            {hasVideos && <Icon name={expanded ? 'caret-down' : 'caret-right'} size="sm" color="tertiary" />}
          </span>
          <span className={`oc-metric-table__platform oc-metric-table__platform--${meta?.platform ?? 'other'}`}>{platform}</span>
          <b>{handle}</b>
        </td>
        <td className="oc-metric-table__cell"><b>{target ? `${posts}/${target}` : posts}</b></td>
        <td className="oc-metric-table__cell"><b>{fmtViewsCompact(views)}</b></td>
        {showMedian && <td className="oc-metric-table__cell"><b>{median == null ? '—' : fmtViewsCompact(median)}</b></td>}
        {days.map((day) => (
          <AccountDayCell key={day} cell={account.cells[day]} highlighted={day === hlDay} />
        ))}
      </tr>
      {expanded && [...account.videos]
        .sort((a, b) => String(a.publishedAt ?? '').localeCompare(String(b.publishedAt ?? ''))
          || String(a.videoId).localeCompare(String(b.videoId)))
        .map((video) => (
          <VideoRow key={video.videoId} video={video} days={days} hlDay={hlDay} showMedian={showMedian} />
        ))}
    </>
  );
}

/**
 * @param {object} props
 * @param {object} props.matrix    — daily-endpoint `matrix` object
 * @param {Array}  props.accounts  — sibling `accounts` meta ([{accountId, handle, platform, posts}])
 * @param {number} [props.target]  — targetPosts per account (Посты column denominator)
 * @param {string|Date|null} [props.closedAt] — closed window: highlight closing day instead of today
 * @param {Map<string, number|null>} [props.medians] — accountId → the window's median views
 *        per video. PRESENT ⇒ a «Медиана» column joins the fixed left columns; absent ⇒
 *        the table renders exactly as before (MON-AMB-UX FX1: the amb row expansion feeds
 *        it from the /monthly payload it already has, which is what let the separate
 *        per-account block go).
 * @param {number|null} [props.totalMedian] — the same number for the «Итого» row.
 */
export function MetricTable ({ matrix, accounts = [], target, closedAt, medians, totalMedian, formatColumn = fmtDayShort }) {
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  if (!matrix || !matrix.days?.length) return null;

  const days = matrix.days;
  const today = new Date().toISOString().slice(0, 10);
  const closedDay = closedAt
    ? (closedAt instanceof Date ? closedAt.toISOString() : String(closedAt)).slice(0, 10)
    : null;
  const hlDay = closedDay ?? today;
  const hlSuffix = closedDay ? ' · закрытие' : ' · сегодня';

  const metaById = new Map(accounts.map((a) => [a.accountId, a]));
  const toggle = (id) => setExpandedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const totals = matrix.totals ?? { cells: {}, postsPublished: 0, viewsDelta: 0 };
  const totalTarget = target ? target * matrix.accounts.length : null;
  const showMedian = medians != null;

  return (
    <div className="oc-metric-table-wrap">
      <table className="oc-metric-table">
        <thead>
          <tr>
            <th className="oc-metric-table__th--sticky">Аккаунт / видео</th>
            <th>Посты</th>
            <th>Просмотры</th>
            {showMedian && <th>Медиана</th>}
            {days.map((day) => (
              <th key={day} className={day === hlDay ? 'oc-metric-table__th--hl' : undefined}>
                {formatColumn(day)}{day === hlDay ? hlSuffix : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.accounts.map((account) => (
            <AccountRows
              key={account.accountId}
              account={account}
              meta={metaById.get(account.accountId)}
              days={days}
              hlDay={hlDay}
              target={target}
              expanded={expandedIds.has(account.accountId)}
              onToggle={() => toggle(account.accountId)}
              showMedian={showMedian}
              median={showMedian ? medians.get(account.accountId) ?? null : null}
            />
          ))}
          <tr className="oc-metric-table__row oc-metric-table__row--total">
            <td className="oc-metric-table__td--sticky"><b>Итого</b></td>
            <td className="oc-metric-table__cell"><b>{totalTarget ? `${totals.postsPublished}/${totalTarget}` : totals.postsPublished}</b></td>
            <td className="oc-metric-table__cell"><b>{fmtViewsCompact(totals.viewsDelta)}</b></td>
            {showMedian && <td className="oc-metric-table__cell"><b>{totalMedian == null ? '—' : fmtViewsCompact(totalMedian)}</b></td>}
            {days.map((day) => (
              <AccountDayCell key={day} cell={totals.cells[day]} highlighted={day === hlDay} />
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
